import { useId, type ReactElement } from "react";
import type { ProblemStatementParams } from "@playbookai/widget-schemas";
import { Textarea } from "../../atoms/Textarea/Textarea.js";
import type { WidgetBodyProps } from "../types.js";
import { useFieldEditSession } from "../field-edit-session.js";
import { useLimitedTextDraft } from "../text-limit.js";
import { PROBLEM_STATEMENT_MAX_LENGTH } from "./limit.js";

/**
 * The panel-size Problem Statement editor.
 *
 * It used to pass `maxLength` to the atom, which made the browser truncate a
 * long paste at the cap with nothing said — the text LOOKED saved (PB-1575).
 * It now shares the card's draft behaviour, so the two surfaces answer an
 * over-limit edit the same way: keep it, refuse to commit it, say why.
 */
export function ProblemStatementExpandedEditor({
  data,
  onChange,
  readOnly,
}: WidgetBodyProps<ProblemStatementParams>): ReactElement {
  const isEmpty = data.problem.trim().length === 0;
  const messageId = useId();
  const countId = useId();
  const draft = useLimitedTextDraft(
    data.problem,
    PROBLEM_STATEMENT_MAX_LENGTH,
    (problem) => onChange({ ...data, problem }),
  );
  const invalid = draft.message !== null;
  /**
   * PB-4923 AC5. This is the surface the whole change came from: the Slot B
   * reproduction lost `OWNER-ALPHA` from a problem statement, and the draft
   * held here is exactly the unsaved text an approved AI change would land on.
   * COMPOSED with the draft's own focus/blur rather than replacing them — the
   * draft uses them to decide when to re-seed from the committed value.
   */
  const lease = useFieldEditSession("problem");

  return (
    <div className="mx-auto flex h-full min-h-0 w-full max-w-5xl flex-col gap-4 text-pb-text">
      <div className="shrink-0 text-xs text-pb-text-muted">
        {readOnly
          ? "Read-only"
          : invalid
            ? "Not saved — see below"
            : "Changes save automatically"}
      </div>
      <div className="flex min-h-0 flex-1 flex-col rounded-xl border border-pb-cyan/20 bg-pb-cyan/[0.06] p-5 sm:p-8">
        <div className="text-dense-1 uppercase tracking-wider font-semibold text-pb-cyan-text">
          Problem
        </div>
        <div
          className={
            readOnly
              ? "mt-4 min-h-0 flex-1 max-w-[60ch]"
              : "mt-4 flex min-h-0 flex-1 flex-col"
          }
        >
          {readOnly ? (
            isEmpty ? (
              <p className="text-body-large italic text-pb-text-muted">
                No problem statement yet — ask PlayerAI to draft one.
              </p>
            ) : (
              <p className="whitespace-pre-wrap break-words text-heading-1 text-pb-text sm:text-display">
                {data.problem}
              </p>
            )
          ) : (
            <>
              <Textarea
                aria-label="Problem statement"
                // No `maxLength`: the atom would pass it to the DOM, and the
                // browser truncates rather than warning. The counter below is
                // driven from the draft instead, so the cap is stated without
                // being silently enforced.
                showCount={false}
                value={draft.value}
                aria-invalid={invalid ? true : undefined}
                // The counter is ALWAYS rendered here (unlike the card, which
                // reveals it near the limit), so it is always part of the
                // field's description; the over-limit message joins it when
                // there is one. Rendered as loose siblings they were visible
                // and unannounced.
                aria-describedby={invalid ? `${countId} ${messageId}` : countId}
                wrapperClassName="flex min-h-0 flex-1 flex-col"
                onChange={(event) => draft.onChange(event.target.value)}
                onFocus={() => {
                  lease.onFocus();
                  draft.onFocus();
                }}
                onBlur={() => {
                  lease.onBlur();
                  draft.onBlur();
                }}
                placeholder="Frame the problem — what's happening, where, and what's the impact?"
                className="min-h-0 flex-1 resize-none border-0 bg-transparent px-0 py-0 text-heading-1 text-pb-text focus:border-0 focus:ring-2 focus:ring-pb-teal/30 sm:text-display"
              />
              <div className="mt-2 flex shrink-0 items-start justify-between gap-4">
                {/* role="alert" so crossing the limit is announced mid-edit. */}
                {invalid ? (
                  <p
                    id={messageId}
                    role="alert"
                    className="text-xs text-pb-red-text"
                  >
                    {draft.message}
                  </p>
                ) : (
                  <span />
                )}
                <p
                  id={countId}
                  className={`shrink-0 text-xs pb-tabular ${
                    invalid ? "text-pb-red-text" : "text-pb-text-muted"
                  }`}
                >
                  {draft.length.toLocaleString()} /{" "}
                  {PROBLEM_STATEMENT_MAX_LENGTH.toLocaleString()}
                </p>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
